<script setup lang="ts">
import type { ListingItemProduct } from '@/types/ListingItems'
import { productIncludes } from '~/utils/includes/product'

const props = withDefaults(defineProps<{
    model: 'related' | 'personal'
    containerId: string
    limit?: number
    itemListId?: string
    itemListName?: string
}>(), {
    limit: 5,
    itemListId: 'emarsys_recommendations',
    itemListName: 'Emarsys Recommendations'
})

const nuxtApp = useNuxtApp()
const { apiClient } = useShopwareContext()

const isLoading = ref(true)
const products = ref<ListingItemProduct[]>([])
const computedItemListName = computed(() => {
    let name = props.itemListName

    switch (props?.model) {
    case 'related':
        name += ' Related'
        break;
    case 'personal':
        name += ' Personal'
        break;
    default:
        name += ''
    }

    return name
})

async function fetchRecommendations(productSkus: string[]) {
    if (productSkus.length === 0) {
        products.value = []
        isLoading.value = false
        return
    }

    try {
        const { data } = await apiClient.invoke('readProduct post /product', {
            body: {
                includes: productIncludes,
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'productNumber',
                        value: productSkus.join('|')
                    }
                ],
                limit: productSkus.length,
                associations: {
                    media: {
                        associations: {
                            media: {}
                        }
                    },
                    cover: { associations: { media: {} } },
                    options: { associations: { group: {} } },
                    properties: { associations: { group: {} } },
                    seoUrls: {}
                }
            }
        })

        // Reorder to match the original SKU order from Emarsys
        const orderedProducts = productSkus
            .map(sku => data.elements?.find(p => p.productNumber === sku))
            .filter((p): p is NonNullable<typeof p> => p !== undefined)

        products.value = orderedProducts.map((product, index) => ({
            pos: index,
            type: 'product' as const,
            data: product
        }))
    } catch (error) {
        console.error('[Emarsys] Error fetching Shopware products:', error)
        products.value = []
    } finally {
        isLoading.value = false
    }
}

let fallbackTimer: ReturnType<typeof setTimeout> | undefined

// Captured at mount, because Vue clears template refs before onUnmounted runs.
const scarabContainer = ref<HTMLElement | null>(null)
let containerEl: HTMLElement | null = null

onMounted(() => {
    containerEl = scarabContainer.value
    nextTick(() => {
        nuxtApp.callHook('renderEmarsysWidget', {
            containerId: props.containerId,
            model: props.model,
            limit: props.limit,
            successCallback: (SC) => {
                const productSkus = (SC?.page?.products ?? [])
                    .map(product => product.item ?? product.id)
                    .filter((sku): sku is string => sku != null)
                fetchRecommendations(productSkus)
            },
            errorCallback: () => {
                console.warn('[Emarsys] Recommendations failed to load')
                isLoading.value = false
            }
        })

        // Fallback timeout in case callbacks don't fire
        fallbackTimer = setTimeout(() => {
            if (isLoading.value) {
                isLoading.value = false
            }
        }, 5000)
    })
})

onUnmounted(() => {
    clearTimeout(fallbackTimer)

    // Emarsys holds on to the container element it was handed for the lifetime of the
    // page. A DOM node keeps its ancestors reachable through parentNode, so that one
    // reference pins this whole product page's detached subtree. Detaching the
    // container leaves Emarsys holding a single orphan <div> instead.
    containerEl?.remove()
    containerEl = null
})
</script>

<template>
    <div class="emarsys-recommender-wrapper">
        <div
            :id="containerId"
            ref="scarabContainer"
            class="hidden"
        />

        <div
            v-if="isLoading"
            class="my-8"
        >
            <div class="skeleton h-10 w-64 mb-4" />
            <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <div
                    v-for="i in 6"
                    :key="i"
                    class="skeleton h-64"
                />
            </div>
        </div>

        <template v-else-if="products.length > 0">
            <h2 class="font-fjalla-one text-4xl font-normal uppercase">
                <template v-if="model === 'related'">
                    {{ $t('emarsys.recommendations.titleRelatedProducts') }}
                </template>
                <template v-else-if="model === 'personal'">
                    {{ $t('emarsys.recommendations.titlePersonalProducts') }}
                </template>
            </h2>

            <div class="my-8">
                <ProductSlider
                    :products="products"
                    :product-qty="{ value: '6', source: 'value' }"
                    :slider-id="containerId"
                    :slider-title="`Emarsys ${model} recommendations`"
                    :item-list-id="itemListId"
                    :item-list-name="computedItemListName"
                    :is-offcanvas="false"
                />
            </div>
        </template>
    </div>
</template>